import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import {
    completeLearnerContentRequest,
    fetchModulePlayerRequest,
    resetModuleLearningState,
    setCurrentLearnerContent,
    startLearnerContentRequest,
    startLearnerModuleRequest,
    updateLearnerContentProgressRequest,
} from "../../../redux/actions/moduleLearningActions";
import ContentRenderer from "./components/ContentRenderer";
import ContentPersonalNotesPanel from "./components/ContentPersonalNotesPanel";
import ContentQuestionsPanel from "./components/ContentQuestionsPanel";
import CourseSidebar from "./components/CourseSidebar";
import ProgressHeader from "./components/ProgressHeader";
import "./ModulePlayerPage.css";

const flattenContents = (sections) =>
    (sections || []).flatMap((section) => section.contents || []);

const findFirstVisibleContent = (sections) => {
    const allContents = flattenContents(sections);
    return allContents.find((content) => !content.locked) || allContents[0] || null;
};

const normalizeTrackPayload = (payload = {}) => {
    if (!payload || typeof payload !== "object") {
        return {};
    }

    const sortedKeys = Object.keys(payload).sort();
    return sortedKeys.reduce((accumulator, key) => {
        accumulator[key] = payload[key];
        return accumulator;
    }, {});
};

const buildTrackSignature = ({ completionPercent = 0, progressPayload = {}, timeSpentDeltaSeconds = 0 }) =>
    JSON.stringify({
        completionPercent: Number(completionPercent || 0),
        timeSpentDeltaSeconds: Number(timeSpentDeltaSeconds || 0),
        progressPayload: normalizeTrackPayload(progressPayload),
    });

const createSessionKey = (contentId) => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `content-${contentId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export default function ModulePlayerPage() {
    const dispatch = useDispatch();
    const location = useLocation();
    const { moduleId } = useParams();
    const {
        player,
        sections,
        loading,
        error,
        currentContentId,
        saveStatus,
        saveError,
        saveContentId,
    } = useSelector((state) => state.moduleLearning || {});
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [scormSidebarState, setScormSidebarState] = useState(null);
    const [scormNodeRequest, setScormNodeRequest] = useState(null);
    const [contentContext, setContentContext] = useState(null);
    const [contentNavigationRequest, setContentNavigationRequest] = useState(null);
    const trackingSessionRef = useRef(null);
    const pendingProgressRef = useRef(null);
    const flushTimerRef = useRef(null);
    const currentContentRef = useRef(null);
    const contentProgressByIdRef = useRef(new Map());
    const lastSentProgressRef = useRef(new Map());
    const lastCompletedContentIdRef = useRef(null);
    const lastStartedContentIdRef = useRef(null);
    const handledPersonalNoteNavigationRef = useRef(null);

    useEffect(() => {
        if (!moduleId) return undefined;

        dispatch(fetchModulePlayerRequest(moduleId));
        dispatch(startLearnerModuleRequest(moduleId));

        return () => {
            dispatch(resetModuleLearningState());
        };
    }, [dispatch, moduleId]);

    const clearScheduledFlush = useCallback(() => {
        if (flushTimerRef.current) {
            window.clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
        }
    }, []);

    const ensureTrackingSession = useCallback((contentId) => {
        if (!trackingSessionRef.current || String(trackingSessionRef.current.contentId) !== String(contentId)) {
            trackingSessionRef.current = {
                contentId,
                sessionKey: createSessionKey(contentId),
                sequenceNumber: 0,
            };
        }
        return trackingSessionRef.current;
    }, []);

    const buildSequencedPayload = useCallback((contentId, payload = {}) => {
        const session = ensureTrackingSession(contentId);
        session.sequenceNumber += 1;
        return {
            ...payload,
            sessionKey: session.sessionKey,
            clientSequenceNumber: session.sequenceNumber,
            clientSentAt: new Date().toISOString(),
        };
    }, [ensureTrackingSession]);

    const flushPendingProgress = useCallback(() => {
        if (!moduleId || !pendingProgressRef.current) return;

        const pending = pendingProgressRef.current;
        const persistedProgress = contentProgressByIdRef.current.get(String(pending.contentId)) || {};
        const isCompleted = String(persistedProgress.status || "").toUpperCase() === "COMPLETED"
            || String(lastCompletedContentIdRef.current) === String(pending.contentId);
        const sameCompletion = Number(pending.completionPercent || 0) === Number(persistedProgress.progressPercent || 0);
        const samePayload = JSON.stringify(normalizeTrackPayload(pending.progressPayload || {}))
            === JSON.stringify(normalizeTrackPayload(persistedProgress.progressPayload || {}));
        const hasNoDelta = Number(pending.timeSpentDeltaSeconds || 0) <= 0;
        const signature = buildTrackSignature(pending);

        if (
            isCompleted
            || (sameCompletion && samePayload && hasNoDelta)
            || (hasNoDelta && lastSentProgressRef.current.get(String(pending.contentId)) === signature)
        ) {
            pendingProgressRef.current = null;
            clearScheduledFlush();
            return;
        }

        pendingProgressRef.current = null;
        clearScheduledFlush();
        lastSentProgressRef.current.set(String(pending.contentId), signature);

        dispatch(
            updateLearnerContentProgressRequest(
                moduleId,
                pending.contentId,
                buildSequencedPayload(pending.contentId, {
                    timeSpentDeltaSeconds: pending.timeSpentDeltaSeconds || 0,
                    completionPercent: pending.completionPercent || 0,
                    progressPayload: pending.progressPayload || {},
                })
            )
        );
    }, [buildSequencedPayload, clearScheduledFlush, dispatch, moduleId]);

    const discardPendingProgress = useCallback((contentId = null) => {
        clearScheduledFlush();
        if (!pendingProgressRef.current) {
            return;
        }
        if (contentId == null || String(pendingProgressRef.current.contentId) === String(contentId)) {
            pendingProgressRef.current = null;
        }
    }, [clearScheduledFlush]);

    const scheduleFlush = useCallback(() => {
        clearScheduledFlush();
        flushTimerRef.current = window.setTimeout(() => {
            flushPendingProgress();
        }, 4000);
    }, [clearScheduledFlush, flushPendingProgress]);

    const activateContent = useCallback((content, { closeSidebar = false } = {}) => {
        if (!content) return;

        flushPendingProgress();
        dispatch(setCurrentLearnerContent(content.id));

        if (closeSidebar) {
            setSidebarOpen(false);
        }

        if (!content.locked) {
            trackingSessionRef.current = {
                contentId: content.id,
                sessionKey: createSessionKey(content.id),
                sequenceNumber: 0,
            };
            if (String(lastStartedContentIdRef.current) !== String(content.id)) {
                lastStartedContentIdRef.current = content.id;
                dispatch(startLearnerContentRequest(moduleId, content.id, buildSequencedPayload(content.id)));
            }
        }
    }, [buildSequencedPayload, dispatch, flushPendingProgress, moduleId]);

    const currentContent = useMemo(() => {
        const allContents = flattenContents(sections);
        return allContents.find((content) => String(content.id) === String(currentContentId)) || null;
    }, [sections, currentContentId]);

    useEffect(() => {
        currentContentRef.current = currentContent;
        if (String(currentContent?.progress?.status || "").toUpperCase() === "COMPLETED") {
            lastCompletedContentIdRef.current = currentContent?.id;
        }
    }, [currentContent]);

    useEffect(() => {
        const nextMap = new Map();
        flattenContents(sections).forEach((content) => {
            nextMap.set(String(content.id), content.progress || {});
        });
        contentProgressByIdRef.current = nextMap;
    }, [sections]);

    useEffect(() => {
        if (!player || !sections?.length) return;

        const allContents = flattenContents(sections);
        if (!allContents.length) return;

        const selectedContent = allContents.find((content) => String(content.id) === String(currentContentId));
        if (selectedContent && !selectedContent.locked) {
            return;
        }

        const preferredContent =
            allContents.find((content) => String(content.id) === String(player.lastContentId) && !content.locked) ||
            findFirstVisibleContent(sections);

        if (preferredContent && String(preferredContent.id) !== String(currentContentId)) {
            activateContent(preferredContent);
        }
    }, [activateContent, currentContentId, player, sections]);

    useEffect(() => {
        const note = location.state?.personalNoteNavigation;
        if (!note?.contentId || !sections?.length) return;
        const allContents = flattenContents(sections);
        const targetContent = allContents.find((content) => String(content.id) === String(note.contentId));
        if (targetContent && String(currentContentId) !== String(targetContent.id)) {
            activateContent(targetContent);
        }
    }, [activateContent, currentContentId, location.state, sections]);

    const handleSelectContent = (content) => {
        if (String(content?.id) === String(currentContentRef.current?.id)) {
            setSidebarOpen(false);
            return;
        }
        activateContent(content, { closeSidebar: true });
    };

    const handleSelectScormNode = useCallback((target) => {
        const nodeId = target && typeof target === "object" ? target.nodeId : target;
        const scoId = target && typeof target === "object" ? target.scoId : null;
        if (!nodeId && !scoId) return;
        setScormNodeRequest({
            nodeId,
            scoId,
            requestId: `${nodeId || "sco"}-${scoId || "node"}-${Date.now()}`,
        });
        setSidebarOpen(false);
    }, []);

    const handleNavigateQuestionContext = useCallback((question) => {
        if (!question) return;

        if (question.scormNodeId) {
            setScormNodeRequest({
                nodeId: question.scormNodeId,
                scoId: null,
                requestId: `qr-${question.scormNodeId}-${Date.now()}`,
            });
        }

        setContentNavigationRequest({
            requestId: `qr-context-${question.id}-${Date.now()}`,
            contentType: question.contentTypeContext,
            videoTimestampSeconds: question.videoTimestampSeconds,
            pdfPage: question.pdfPage,
            scormNodeId: question.scormNodeId,
            scormNodeTitle: question.scormNodeTitle,
            scrollPosition: question.scrollPosition,
        });
    }, []);

    const handleNavigateNoteContext = useCallback((note) => {
        if (!note) return;

        if (note.scormNodeId) {
            setScormNodeRequest({
                nodeId: note.scormNodeId,
                scoId: null,
                requestId: `note-${note.scormNodeId}-${Date.now()}`,
            });
        }

        setContentNavigationRequest({
            requestId: `note-context-${note.id}-${Date.now()}`,
            contentType: note.noteContextType,
            videoTimestampSeconds: note.videoTimestampSeconds,
            pdfPage: note.pdfPage,
            scormNodeId: note.scormNodeId,
            scormNodeTitle: note.scormNodeTitle,
            scrollPosition: note.scrollPosition,
        });
    }, []);

    useEffect(() => {
        const note = location.state?.personalNoteNavigation;
        if (!note?.id || !currentContent || String(note.contentId) !== String(currentContent.id)) return;
        if (handledPersonalNoteNavigationRef.current === String(note.id)) return;
        handledPersonalNoteNavigationRef.current = String(note.id);
        setActiveTab("notes");
        handleNavigateNoteContext(note);
    }, [currentContent, handleNavigateNoteContext, location.state]);

    const handleScormNavigationChange = useCallback((navigationState) => {
        setScormSidebarState(navigationState);
    }, []);

    useEffect(() => {
        if (String(currentContent?.contentType || "").toUpperCase() !== "SCORM") {
            setScormSidebarState(null);
            setScormNodeRequest(null);
        }
        setContentContext(null);
        setContentNavigationRequest(null);
    }, [currentContent?.contentType, currentContent?.id]);

    const handleTrackContent = useCallback((update = {}) => {
        const activeContent = currentContentRef.current;
        if (!activeContent || activeContent.locked) {
            return;
        }

        if (String(activeContent.progress?.status || "").toUpperCase() === "COMPLETED") {
            lastCompletedContentIdRef.current = activeContent.id;
            return;
        }

        if (pendingProgressRef.current && String(pendingProgressRef.current.contentId) !== String(activeContent.id)) {
            flushPendingProgress();
        }

        const currentPending = pendingProgressRef.current || {
            contentId: activeContent.id,
            timeSpentDeltaSeconds: 0,
            completionPercent: Number(activeContent.progress?.progressPercent || 0),
            progressPayload: {
                ...(activeContent.progress?.progressPayload || {}),
            },
        };

        const nextPending = {
            contentId: activeContent.id,
            timeSpentDeltaSeconds: Number(currentPending.timeSpentDeltaSeconds || 0) + Number(update.timeSpentDeltaSeconds || 0),
            completionPercent: Math.max(Number(currentPending.completionPercent || 0), Number(update.completionPercent || 0)),
            progressPayload: {
                ...(currentPending.progressPayload || {}),
                ...(update.progressPayload || {}),
            },
        };

        const persistedProgress = contentProgressByIdRef.current.get(String(activeContent.id)) || {};
        const sameCompletion = Number(nextPending.completionPercent || 0) === Number(persistedProgress.progressPercent || 0);
        const samePayload = JSON.stringify(normalizeTrackPayload(nextPending.progressPayload || {}))
            === JSON.stringify(normalizeTrackPayload(persistedProgress.progressPayload || {}));
        const hasNoDelta = Number(nextPending.timeSpentDeltaSeconds || 0) <= 0;

        if (sameCompletion && samePayload && hasNoDelta) {
            return;
        }

        pendingProgressRef.current = nextPending;

        if (update.immediate) {
            flushPendingProgress();
        } else {
            scheduleFlush();
        }
    }, [flushPendingProgress, scheduleFlush]);

    const handleCompleteContent = (content) => {
        if (!content || String(content.progress?.status || "").toUpperCase() === "COMPLETED") {
            lastCompletedContentIdRef.current = content?.id || lastCompletedContentIdRef.current;
            return;
        }
        if (String(lastCompletedContentIdRef.current) === String(content.id)) {
            return;
        }
        lastCompletedContentIdRef.current = content.id;
        discardPendingProgress(content.id);
        dispatch(completeLearnerContentRequest(moduleId, content.id, buildSequencedPayload(content.id)));
    };

    const handleRefreshPlayer = useCallback(() => {
        if (!moduleId) return;
        dispatch(fetchModulePlayerRequest(moduleId));
    }, [dispatch, moduleId]);

    useEffect(() => () => {
        clearScheduledFlush();
        flushPendingProgress();
    }, [clearScheduledFlush, flushPendingProgress]);

    useEffect(() => {
        const flushOnPageExit = () => flushPendingProgress();
        const handleVisibilityChange = () => {
            if (document.hidden) {
                flushPendingProgress();
            }
        };

        window.addEventListener("beforeunload", flushOnPageExit);
        window.addEventListener("pagehide", flushOnPageExit);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("beforeunload", flushOnPageExit);
            window.removeEventListener("pagehide", flushOnPageExit);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [flushPendingProgress]);

    const [activeTab, setActiveTab] = useState("presentation");
    const tabs = [
        { id: "presentation", label: "Presentation" },
        { id: "qr", label: "Q/R" },
        { id: "notes", label: "Mes notes" },
    ];

    const renderTabContent = () => {
        if (activeTab === "presentation") {
            const progress = player?.progress || {};
            const totalContents = flattenContents(sections).length;
            return (
                <section className="lp-tab-presentation">
                    <header className="lp-presentation-header">
                        <h2>A propos de ce cours</h2>
                        <p>{player?.description || "Ce module de formation vous permet d'acquerir des competences specifiques dans votre domaine."}</p>
                    </header>

                    <div className="lp-presentation-grid">
                        <article className="lp-presentation-main">
                            <div className="lp-info-row">
                                <strong>Sections :</strong> {sections?.length || 0}
                            </div>
                            <div className="lp-info-row">
                                <strong>Contenus :</strong> {totalContents}
                            </div>
                            {player?.duration && (
                                <div className="lp-info-row">
                                    <strong>Durée estimée :</strong> {player.duration}
                                </div>
                            )}
                            {player?.hasCertificate && (
                                <div className="lp-info-row">
                                    <strong>Certificat :</strong> Disponible à la fin du parcours
                                </div>
                            )}
                        </article>

                        <aside className="lp-presentation-side">
                            <div className="lp-stats-card">
                                <div className="lp-stats-percentage">{progress.progressPercent || 0}%</div>
                                <div className="lp-stats-label">Progression globale</div>
                                <div className="lp-stats-count">{progress.completedRequiredContentsCount || 0} / {progress.totalRequiredContentsCount || 0} requis terminés</div>
                            </div>
                        </aside>
                    </div>

                    {player?.formationNom && (
                        <footer className="lp-presentation-footer">
                            <span>Fait partie de la formation : <strong>{player.formationNom}</strong></span>
                        </footer>
                    )}
                </section>
            );
        }
        if (activeTab === "qr") {
            return (
                <ContentQuestionsPanel
                    moduleId={moduleId}
                    content={currentContent}
                    context={contentContext || { contentTypeContext: currentContent?.contentType }}
                    onNavigateContext={handleNavigateQuestionContext}
                />
            );
        }
        if (activeTab === "notes") {
            return (
                <ContentPersonalNotesPanel
                    moduleId={moduleId}
                    content={currentContent}
                    context={contentContext || { contentTypeContext: currentContent?.contentType }}
                    onNavigateContext={handleNavigateNoteContext}
                />
            );
        }
        return null;
    };

    return (
        <main className="lp-page">
            <ProgressHeader
                moduleItem={player}
                currentContent={currentContent}
                onToggleSidebar={() => setSidebarOpen((value) => !value)}
                sidebarOpen={sidebarOpen}
            />

            <section className="lp-layout">
                <section className="lp-main">
                    <section className="lp-viewer-container" aria-live="polite">
                        {loading && !player ? <div className="lp-state">Chargement du player...</div> : null}
                        {error && !player ? <div className="lp-state lp-state--error">{error}</div> : null}
                        {!loading && !error && !currentContent ? (
                            <div className="lp-state">Aucun contenu disponible dans ce module.</div>
                        ) : null}

                        {currentContent ? (
                            <ContentRenderer
                                key={currentContent.id}
                                moduleItem={player}
                                content={currentContent}
                                onComplete={() => handleCompleteContent(currentContent)}
                                onTrack={handleTrackContent}
                                onRefreshPlayer={handleRefreshPlayer}
                                scormNodeRequest={scormNodeRequest}
                                navigationRequest={contentNavigationRequest}
                                onScormNavigationChange={handleScormNavigationChange}
                                onContextChange={setContentContext}
                                saveStatus={String(saveContentId) === String(currentContent.id) ? saveStatus : "idle"}
                                saveError={String(saveContentId) === String(currentContent.id) ? saveError : null}
                                hideHeader={true}
                            />
                        ) : null}
                    </section>

                    <section className="lp-tabs-section" aria-label="Informations du parcours">
                        <nav className="lp-tabs-header" aria-label="Onglets du parcours">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={`lp-tab-btn ${activeTab === tab.id ? "is-active" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                        <section className="lp-tabs-body">
                            {renderTabContent()}
                        </section>
                    </section>
                </section>

                <CourseSidebar
                    moduleItem={player}
                    sections={sections}
                    currentContentId={currentContentId}
                    onSelectContent={handleSelectContent}
                    scormNavigation={scormSidebarState}
                    onSelectScormNode={handleSelectScormNode}
                    sidebarOpen={sidebarOpen}
                    onCloseSidebar={() => setSidebarOpen(false)}
                />
            </section>

            {sidebarOpen ? <button type="button" className="lp-backdrop" onClick={() => setSidebarOpen(false)} /> : null}
        </main>
    );
}
